import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { deviceDetect } from 'react-device-detect';
import { handleMonitoringFrontendError } from '@/shared/api';
import { auth } from '@/shared/lib';
import { useEmitter } from '@/shared/lib/customHooks';
import { FRONT_END_ERRORS, FRONT_END_ERRORS_EMITTER_CONSTANTS } from '@/shared/config';
import packageJson from '../../../../../package.json';
import {
  API_SPAM_THRESHOLD,
  API_SPAM_TIMEOUT,
  SOCKET_SPAM_THRESHOLD,
  SOCKET_SPAM_TIMEOUT,
} from './spamTracking.c';

/**
 * Watches the portal for runaway GraphQL calls and socket events and files a front-end monitoring report when either floods. Emitter subscriptions accumulate operation and event names into refs, one-minute timers count them and report any name over its threshold together with device, portal and release details, a sessionStorage flag stops a second report per session, and unmount plus beforeunload clear the timers and flags.
 *
 * @returns Null, since the tracker renders no markup.
 */
const SpamTracking = (): null => {
  const deviceInfo = deviceDetect(undefined);

  const portalVersion = packageJson?.version || 'unknown';
  const releaseVersion = packageJson?.versionDisplay || 'unknown';

  const apiTrackingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const apiTrackingOperations = useRef<string[]>([]);
  const socketTrackingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const socketTrackingEvents = useRef<string[]>([]);

  const API_SPAM_REPORTED_KEY = 'spam:api:reported';
  const SOCKET_SPAM_REPORTED_KEY = 'spam:socket:reported';

  /**
   * Checks whether a spam category has already been reported in this browser session. The sessionStorage entry for the key is compared against the string '1'.
   *
   * @param key - SessionStorage key of the spam category.
   * @returns True when the category has already been reported.
   */
  const hasReported = (key: string) => sessionStorage.getItem(key) === '1';

  /**
   * Marks a spam category as reported so the session does not send it twice. The string '1' is written to sessionStorage under the key.
   *
   * @param key - SessionStorage key of the spam category.
   * @returns Nothing.
   */
  const markReported = (key: string) => sessionStorage.setItem(key, '1');

  /**
   * Forgets that API and socket spam have already been reported this session. Both sessionStorage flags are removed, which is what lets a reload report again.
   *
   * @returns Nothing.
   */
  const clearReportedFlags = () => {
    sessionStorage.removeItem(API_SPAM_REPORTED_KEY);
    sessionStorage.removeItem(SOCKET_SPAM_REPORTED_KEY);
  };

  /**
   * Cancels the pending window that would report API call spam. The stored timer is cleared and its ref reset to null so the next tracked call opens a fresh window.
   *
   * @returns Nothing.
   */
  const clearApiTrackingTimeout = () => {
    if (apiTrackingTimeout.current) {
      clearTimeout(apiTrackingTimeout.current);
      apiTrackingTimeout.current = null;
    }
  };

  /**
   * Cancels the pending window that would report socket event spam. The stored timer is cleared and its ref reset to null so the next tracked event opens a fresh window.
   *
   * @returns Nothing.
   */
  const clearSocketTrackingTimeout = () => {
    if (socketTrackingTimeout.current) {
      clearTimeout(socketTrackingTimeout.current);
      socketTrackingTimeout.current = null;
    }
  };

  /**
   * Closes an API rate-limit window and reports any operation called too often. The pending list is drained and its timer cleared, an empty window or an already-reported session exits early, names are counted and those over the threshold packaged with the window size, device, portal and release details into a monitoring report, and a send failure is logged.
   *
   * @returns A promise resolving once the report has been attempted.
   */
  const handleApiSpamReport = async () => {
    const operations = apiTrackingOperations.current;
    apiTrackingOperations.current = [];
    clearApiTrackingTimeout();

    if (!operations.length) {
      return;
    }

    if (hasReported(API_SPAM_REPORTED_KEY)) {
      return;
    }

    const apiCounts = _.reduce(
      operations,
      (acc: Record<string, number>, name) => {
        acc[name] = (acc[name] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const spamApis = _.map(
      _.filter(_.toPairs(apiCounts), ([, count]) => count > API_SPAM_THRESHOLD),
      ([name, count]) => ({
        name,
        count,
      }),
    );

    if (!spamApis.length) {
      return;
    }

    try {
      markReported(API_SPAM_REPORTED_KEY);
      await handleMonitoringFrontendError({
        exception: JSON.stringify({
          name: 'ApiRateLimit',
          message: 'API called too many times within 1 minute',
          spamApis,
          threshold: API_SPAM_THRESHOLD,
          windowMs: API_SPAM_TIMEOUT,
          deviceInfo,
          portalVersion,
          releaseVersion,
        }),
        type: FRONT_END_ERRORS.API_RATE_LIMIT,
        userId: auth.userId(),
      });
    } catch (error) {
      console.error('Failed to send API rate limit data: ', error);
    }
  };

  /**
   * Records one GraphQL operation towards the API rate-limit window. Operations without a name are ignored, the name is pushed onto the pending list, and the first entry after an idle period starts the timer that later files the spam report.
   *
   * @param operation - The GraphQL operation whose operationName is counted.
   * @returns Nothing.
   */
  const handleApiTrackingEmitter = (operation: any) => {
    const operationName = operation?.operationName;
    if (!operationName) {
      return;
    }

    apiTrackingOperations.current.push(operationName);

    if (!apiTrackingTimeout.current) {
      apiTrackingTimeout.current = setTimeout(handleApiSpamReport, API_SPAM_TIMEOUT);
    }
  };

  /**
   * Closes a socket rate-limit window and reports any event received too often. The pending list is drained and its timer cleared, an empty window or an already-reported session exits early, names are counted and those over the threshold packaged with the window size, device, portal and release details into a monitoring report, and a send failure is logged.
   *
   * @returns A promise resolving once the report has been attempted.
   */
  const handleSocketSpamReport = async () => {
    const events = socketTrackingEvents.current;
    socketTrackingEvents.current = [];
    clearSocketTrackingTimeout();

    if (!events.length) {
      return;
    }

    if (hasReported(SOCKET_SPAM_REPORTED_KEY)) {
      return;
    }

    const eventCounts = _.reduce(
      events,
      (acc: Record<string, number>, name) => {
        acc[name] = (acc[name] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const spamSockets = _.map(
      _.filter(_.toPairs(eventCounts), ([, count]) => count > SOCKET_SPAM_THRESHOLD),
      ([name, count]) => ({
        name,
        count,
      }),
    );

    if (!spamSockets.length) {
      return;
    }

    try {
      markReported(SOCKET_SPAM_REPORTED_KEY);
      await handleMonitoringFrontendError({
        exception: JSON.stringify({
          name: 'SocketRateLimit',
          message: 'Socket event received too many times within 1 minute',
          spamSockets,
          threshold: SOCKET_SPAM_THRESHOLD,
          windowMs: SOCKET_SPAM_TIMEOUT,
          deviceInfo,
          portalVersion,
          releaseVersion,
        }),
        type: FRONT_END_ERRORS.SOCKET_RATE_LIMIT,
        userId: auth.userId(),
      });
    } catch (error) {
      console.error('Failed to send socket rate limit data: ', error);
    }
  };

  /**
   * Records one incoming socket event towards the socket rate-limit window. Unnamed events are ignored, the name is pushed onto the pending list, and the first entry after an idle period starts the timer that later files the spam report.
   *
   * @param socketEventName - Name of the socket event that was received.
   * @returns Nothing.
   */
  const handleSocketTrackingEmitter = (socketEventName: string) => {
    if (!socketEventName) {
      return;
    }

    socketTrackingEvents.current.push(socketEventName);

    if (!socketTrackingTimeout.current) {
      socketTrackingTimeout.current = setTimeout(handleSocketSpamReport, SOCKET_SPAM_TIMEOUT);
    }
  };

  useEmitter(FRONT_END_ERRORS_EMITTER_CONSTANTS.API_SPAM_TRACKING, handleApiTrackingEmitter, []);

  useEmitter(
    FRONT_END_ERRORS_EMITTER_CONSTANTS.SOCKET_SPAM_TRACKING,
    handleSocketTrackingEmitter,
    [],
  );

  useEffect(
    () => () => {
      apiTrackingOperations.current = [];
      clearApiTrackingTimeout();
      socketTrackingEvents.current = [];
      clearSocketTrackingTimeout();
    },
    [],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', clearReportedFlags);
    return () => {
      window.removeEventListener('beforeunload', clearReportedFlags);
    };
  }, []);

  return null;
};

export default SpamTracking;
